<template>
  <div role="dialog" class="popup fadein absolute z-50 flex justify-center items-center">
    <div class="popup-wrapper z-50 w-1/2 xl:w-1/3 max-w-lg h-auto flex flex-row-reverse rounded-xl bg-slate-middle">
      <div
        tabindex="0"
        ref="firstFocus"
        class="absolute h-6 w-6 mr-2 mt-3 p-1.5 bg-black rounded-full flex justify-center items-center cursor-pointer"
        @pointerup.stop="close()"
        @keydown.enter="close()"
      >
        <img class="w-5 h-5 invert rotate-45" :src="Close" />
      </div>
      <div class="flex flex-col items-start w-full h-full p-9">
        <h2 class="ml-1 mb-4 text-slate-white text-2xl font-bold">{{ t('newboard') }}</h2>
        <input
          ref="boardName"
          v-model="state.boardName"
          class="bg-slate-dark p-2 rounded-3xl font-thin text-lg w-full text-slate-white"
          type="text"
        />
        <div class="pt-5 flex justify-between items-center w-full">
          <button class="gray-btn cursor-pointer p-3 px-5 text-md font-medium text-slate-white mr-2 capitalize" @click.prevent="close()">
            {{ t('cancel') }}
          </button>
          <button
            class="blue-btn cursor-pointer p-3 px-5 text-md font-medium text-slate-white capitalize"
            @click="saveAndClose()"
            @touchstart.prevent="saveAndClose()"
          >
            {{ t('save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { reactive, ref, onMounted, nextTick } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { Board, DialogMessageType, Parcel } from '@/common/classes'
  import { ParcelType } from '@/constants'
  import { StoreResponse } from '@/common/interfaces'

  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useParcelStore from '@/composables/parcel'
  import useFilterStore from '@/composables/filter'
  import useDialogStore from '@/composables/dialog'

  import Close from '../../assets/icons/fontawesome/plus-solid.svg'

  const messages = {
    no: {
      boardname: 'Velg et navn',
      boardexists: 'A wall with the same name already exists',
      newboard: 'Legg til en ny gruppetavle',
    },
    en: {
      boardname: 'Choose a name',
      boardexists: 'A wall with the same name already exists',
      newboard: 'Create a new group wall',
    },
  }

  const userStore = useUserStore()
  const wallStore = useWallStore()
  const dialogStore = useDialogStore()
  const parcelStore = useParcelStore()
  const filter = useFilterStore()

  const { t } = useI18n({ messages })

  const boardName = ref<HTMLInputElement | null>(null)

  const emit = defineEmits<{
    (e: 'closePopup'): void
  }>()

  const state = reactive({
    boardName: `${t('boardname')}`,
  })
  const firstFocus = ref()

  onMounted(() => {
    nextTick(() => firstFocus.value?.focus())
  })

  function close() {
    emit('closePopup')
  }

  async function saveAndClose() {
    if (
      wallStore.getters.selectedWall.value &&
      wallStore.getters.selectedWall.value.boards.findIndex((board: Board) => board.name === state.boardName) === -1
    ) {
      await assembleNewBoard()
      close()
    } else {
      dialogStore.actions.pushMessage('Error', DialogMessageType.Error, t('boardexists'))
    }
  }

  async function assembleNewBoard() {
    let updatedWall = wallStore.getters.selectedWall.value // use store value since prop update is unreliable
    if (updatedWall) {
      updatedWall.addBoard({ name: state.boardName, createdAt: new Date() } as Board)
      const res = await wallStore.actions.updateWall(updatedWall)
      if (res.success && updatedWall && wallStore.getters.selectedWall.value) {
        const resultBoard = wallStore.getters.selectedWall.value.boards.find((board: Board) => board.name === state.boardName)
        if (resultBoard && resultBoard._id) {
          wallStore.actions.setSelectedBoard(resultBoard)
          filter.actions.setBoardId(resultBoard._id)
          const parcel = new Parcel({ parcelType: ParcelType.BoardAdded, body: updatedWall }, userStore.getters.user.value, updatedWall)
          await parcelStore.actions.sendParcel(updatedWall._id, parcel)
        } else dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('boardcreationfailed'))
      }
    }
  }
</script>

<style scoped></style>
